/* eslint-disable max-lines */
import { DefaultTrigger } from '@/components/ui/floating-ask-heidi/evidence-settings-default-trigger';
import type { ResolvedShortcut } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import { ShortcutMarketplaceModal } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-modal';
import {
  ALL_CATEGORIES,
  getCategoryIcon,
  type SuggestedChip,
  type SuggestedChipCategory,
} from '@/components/ui/transcript-chips/types';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { cn } from '@/lib/cn';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { skies } from '@heidi/design-tokens';
import { Region } from '@heidi/schema/ml-scribe';
import { LayoutGrid, Pencil, SlashSquare, Sparkles, X } from 'lucide-react';
import { memo, type ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { evidenceSettingsPopoverMessages } from './evidence-settings-popover.messages';
import { suggestionCategoryLabelMessages } from './suggestion-category-label.messages';
import { useShortcutMarketplacePins } from './use-shortcut-marketplace-pins';

export type PinnedChip = SuggestedChip & {
  isDefault?: boolean;
  /** Team shortcut reference id when this pin is team-managed. */
  teamShortcutId?: string;
  /**
   * Origin bucket for analytics. Mirrors EffectivePinnedQuery.origin:
   * - 'default': Heidi-provided preset (also implies isDefault=true)
   * - 'library': user installed from marketplace (includes team shortcuts)
   * - 'custom':  user authored
   * Absent for non-pinned-originating chips; treat as unknown.
   */
  origin?: 'default' | 'library' | 'custom';
};

export interface EvidenceSettingsPopoverProps {
  suggestEnabled: boolean;
  onSuggestEnabledChange: (enabled: boolean) => void;
  autoAnswerPinned?: boolean;
  onAutoAnswerPinnedChange?: (enabled: boolean) => void;
  enabledCategories: Set<SuggestedChipCategory>;
  onCategoryToggle: (category: SuggestedChipCategory) => void;
  permanentChips: PinnedChip[];
  /**
   * Called when a shortcut is installed from the marketplace or newly
   * authored. `origin` distinguishes 'library' (marketplace install) from
   * 'custom' (user-authored); the server uses it for analytics only.
   */
  onAddPermanentChip: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string,
    teamShortcutId?: string
  ) => void;
  onRemovePermanentChip: (chipId: string) => void;
  onEditPermanentChip?: (
    chipId: string,
    label: string,
    query: string,
    description?: string
  ) => void;
  onSavedChipClick?: (chip: PinnedChip) => void;
  onClinicalChipClick?: (category: SuggestedChipCategory) => void;
  onRestoreDefaultShortcut?: (shortcutId: string) => void;
  trigger?: ReactNode;
  triggerTooltip?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
}

export const EvidenceSettingsPopover = memo(
  ({
    suggestEnabled,
    enabledCategories,
    onCategoryToggle,
    permanentChips,
    onAddPermanentChip,
    onRemovePermanentChip,
    onEditPermanentChip,
    onSavedChipClick,
    onClinicalChipClick,
    onRestoreDefaultShortcut,
    trigger,
    triggerTooltip,
    align = 'end',
    side = 'top',
  }: EvidenceSettingsPopoverProps) => {
    const intl = useIntl();
    const [showMarketplace, setShowMarketplace] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const { data: kindeProperties } = useQueryKindeProperties();
    const isUS = kindeProperties?.region === Region.US;
    const { isTeamAdmin, isTeamOwner } = useCombinedQueriesTeamGeneralInfo();
    const canManageTeamShortcuts = !!(isTeamAdmin || isTeamOwner);

    const { pinnedShortcutIds, pinnedShortcutLabels, customShortcuts } =
      useShortcutMarketplacePins(permanentChips);

    const sectionMode = isEditMode ? 'edit' : 'view';

    const handleMarketplaceAdd = (
      shortcut: ResolvedShortcut,
      origin: 'library' | 'custom'
    ) => {
      if (shortcut.id.startsWith('default-') && onRestoreDefaultShortcut) {
        // Restoring a dismissed default keeps its server-assigned origin.
        onRestoreDefaultShortcut(shortcut.id);
      } else {
        onAddPermanentChip(
          shortcut.label,
          shortcut.query,
          origin,
          shortcut.description,
          shortcut.teamShortcutId
        );
      }
    };

    const handleMarketplaceRemove = (shortcut: ResolvedShortcut) => {
      // Find the matching chip by ID (for defaults) or label (for custom)
      const matchingChip = permanentChips.find(
        (chip) =>
          chip.id === shortcut.id ||
          (chip.label === shortcut.label && !shortcut.id.startsWith('default-'))
      );
      if (matchingChip) {
        onRemovePermanentChip(matchingChip.id);
      }
    };

    const renderTrigger = () => {
      if (!trigger) return <DefaultTrigger />;
      if (triggerTooltip) {
        return (
          <TooltipProvider delayDuration={400}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>{trigger}</PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8}>
                {triggerTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      return <PopoverTrigger asChild>{trigger}</PopoverTrigger>;
    };

    return (
      <Popover onOpenChange={(open) => !open && setIsEditMode(false)}>
        {renderTrigger()}
        <PopoverContent
          className="relative w-96 p-0"
          align={align}
          side={side}
          sideOffset={12}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-text-secondary" />
              <span className="text-sm font-medium text-text-primary">
                <FormattedMessage
                  {...evidenceSettingsPopoverMessages.suggestionsHeading}
                />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowMarketplace(true)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-all',
                  'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                )}
              >
                <LayoutGrid size={12} />
                <FormattedMessage {...evidenceSettingsPopoverMessages.browseAll} />
              </button>
              <button
                type="button"
                onClick={() => setIsEditMode(!isEditMode)}
                className={cn(
                  'flex size-6 items-center justify-center rounded-md transition-colors',
                  isEditMode
                    ? 'bg-surface-accent text-text-primary'
                    : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
                )}
                aria-label={intl.formatMessage(
                  isEditMode
                    ? evidenceSettingsPopoverMessages.doneEditingAria
                    : evidenceSettingsPopoverMessages.editShortcutsAria
                )}
                aria-pressed={isEditMode}
              >
                <Pencil size={14} />
              </button>
              <PopoverClose
                className={cn(
                  'flex size-6 items-center justify-center rounded-md transition-colors',
                  'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
                )}
                aria-label={intl.formatMessage(evidenceSettingsPopoverMessages.closeAria)}
              >
                <X size={14} />
              </PopoverClose>
            </div>
          </div>

          <div className="p-4">
            {permanentChips.length > 0 && (
              <div className="mb-4">
                <p className="mb-2 text-xs text-text-tertiary">
                  <FormattedMessage
                    {...evidenceSettingsPopoverMessages.pinnedSectionHeading}
                    values={{ sectionMode }}
                  />
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {permanentChips.map((chip) => {
                    const isTeamChip = !!chip.teamShortcutId;
                    const isReadOnly = isTeamChip && !canManageTeamShortcuts;
                    const showRemoveAffordance = isEditMode && !isReadOnly;
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => {
                          if (isEditMode) {
                            if (isReadOnly) return;
                            onRemovePermanentChip(chip.id);
                          } else {
                            onSavedChipClick?.(chip);
                          }
                        }}
                        className={cn(
                          'group flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors',
                          showRemoveAffordance && 'hover:border-red-400 hover:bg-red-50',
                          isEditMode && isReadOnly && 'cursor-not-allowed opacity-60'
                        )}
                        style={{
                          backgroundColor: skies[50],
                          borderColor: skies[200],
                          color: skies[700],
                        }}
                      >
                        <SlashSquare size={12} />
                        <span>{chip.label}</span>
                        {showRemoveAffordance && (
                          <X
                            size={12}
                            className="ml-0.5 text-text-tertiary transition-colors group-hover:text-red-500"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {suggestEnabled && (
              <div>
                <p className="mb-2 text-xs text-text-tertiary">
                  <FormattedMessage
                    {...evidenceSettingsPopoverMessages.clinicalSectionHeading}
                    values={{ sectionMode }}
                  />
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_CATEGORIES.map((category) => {
                    const isEnabled = enabledCategories.has(category);
                    const CategoryIcon = getCategoryIcon(category);
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => {
                          if (isEditMode) {
                            onCategoryToggle(category);
                          } else if (isEnabled && onClinicalChipClick) {
                            onClinicalChipClick(category);
                          } else {
                            onCategoryToggle(category);
                          }
                        }}
                        className={cn(
                          'flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-all',
                          isEnabled
                            ? 'border-transparent bg-surface-accent text-text-primary hover:bg-surface-accent-hover'
                            : 'border-border bg-surface text-text-tertiary hover:border-border-hover hover:text-text-secondary'
                        )}
                      >
                        <CategoryIcon size={12} />
                        <span>
                          {intl.formatMessage(suggestionCategoryLabelMessages[category])}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!suggestEnabled && permanentChips.length === 0 && (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <Sparkles size={24} className="mb-2 text-text-tertiary" />
                <p className="text-sm text-text-secondary">
                  <FormattedMessage {...evidenceSettingsPopoverMessages.emptyTitle} />
                </p>
                <p className="text-xs text-text-tertiary">
                  <FormattedMessage {...evidenceSettingsPopoverMessages.emptySubtitle} />
                </p>
              </div>
            )}
          </div>
        </PopoverContent>
        <ShortcutMarketplaceModal
          open={showMarketplace}
          onOpenChange={setShowMarketplace}
          pinnedShortcutIds={pinnedShortcutIds}
          pinnedShortcutLabels={pinnedShortcutLabels}
          onAddShortcut={handleMarketplaceAdd}
          onRemoveShortcut={handleMarketplaceRemove}
          customShortcuts={customShortcuts}
          onEditShortcut={onEditPermanentChip}
          isUS={isUS}
        />
      </Popover>
    );
  }
);

EvidenceSettingsPopover.displayName = 'EvidenceSettingsPopover';
